import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
} from '@coreui/react-pro'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import AdminAnnouncementService from 'src/service/AdminAnnouncementService'
import ToastComponent from 'src/components/common/TaostComponent'
import { QUILL_MODULES } from './quillConfig'
import { isEmptyHtml } from 'src/utils/html'

const AddForm = ({ visible, onClose, fetchAnnouncements }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)

  const validationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required').max(100, '100 Character Limit is allowed.'),
    description: Yup.string().test(
      'description-required',
      'Description is required',
      (value) => !isEmptyHtml(value),
    ),
  })

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const formData = new FormData()
      formData.append('title', data.title)
      formData.append('description', data.description)

      setLoader(true)
      AdminAnnouncementService.saveAnnouncement(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchAnnouncements()
            ToastComponent(res.message, 'success')
            actions.resetForm()
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const handleClose = () => {
    formik.resetForm()
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 800 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>New Announcement</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Post an update to your members&apos; feed.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel htmlFor="title">Title *</CFormLabel>
            <CFormInput
              placeholder="Announcement title"
              className={
                'form-control' + (formik.errors.title && formik.touched.title ? ' is-invalid' : '')
              }
              value={formik.values.title}
              onChange={formik.handleChange}
              aria-label="title"
              id="title"
            />
            {formik.errors.title && formik.touched.title && (
              <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
            )}
          </CCol>

          <CCol xs={12}>
            <CFormLabel htmlFor="description">Description *</CFormLabel>
            <div
              className={`an-editor${
                formik.errors.description && formik.touched.description ? ' an-editor--invalid' : ''
              }`}
            >
              <ReactQuill
                theme="snow"
                value={formik.values.description}
                onChange={(content) => formik.setFieldValue('description', content)}
                onBlur={() => formik.setFieldTouched('description', true)}
                modules={QUILL_MODULES}
                placeholder="Enter announcement description"
              />
            </div>
            {formik.errors.description && formik.touched.description && (
              <div className="invalid-feedback d-block">{formik.errors.description}</div>
            )}
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Publish
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
